/**
 * CSV export.
 *
 * Built in the browser from the parsed contract, not fetched again. The data
 * is already on the device, so a download costs no request, no quota and no
 * Flight Reader billing. It also means export still works on a flight opened
 * from the library while offline.
 *
 * The output is the full-resolution log: every row, every column that had
 * data, plus any event text on that row. Deliberately not the decimated view
 * the map draws, because someone exporting a CSV is going to analyse it.
 */

import { fmt } from './contract.js';

/** Columns that lead the file, in a deliberate order. Others follow. */
const LEADING = [
  'timeS',
  'epochS',
  'latitude',
  'longitude',
  'heightM',
  'altitudeM',
  'speedHorizontalMs',
  'speedVerticalMs',
  'homeDistanceM',
  'satellites',
  'gpsLevel',
  'batteryPercent',
  'batteryVoltageV',
];

/** Human headings. Anything absent falls back to the raw column name. */
const HEADINGS = {
  timeS: 'Elapsed [s]',
  epochS: 'Timestamp [UTC]',
  latitude: 'Latitude',
  longitude: 'Longitude',
  heightM: 'Height AGL [m]',
  altitudeM: 'Altitude AMSL [m]',
  speedHorizontalMs: 'Speed [m/s]',
  speedVerticalMs: 'Vertical speed [m/s]',
  homeDistanceM: 'Distance from home [m]',
  satellites: 'Satellites',
  gpsLevel: 'GPS level',
  gpsUsed: 'GPS in use',
  batteryPercent: 'Battery [%]',
  batteryVoltageV: 'Battery voltage [V]',
  batteryCurrentA: 'Battery current [A]',
  batteryTemperatureC: 'Battery temperature [C]',
  batteryCapacityMah: 'Battery capacity [mAh]',
  batteryCellDeviationV: 'Cell deviation [V]',
  batteryCycleCount: 'Charge cycles',
  pitchDeg: 'Pitch [deg]',
  rollDeg: 'Roll [deg]',
  yawDeg: 'Yaw [deg]',
  gimbalPitchDeg: 'Gimbal pitch [deg]',
  gimbalRollDeg: 'Gimbal roll [deg]',
  gimbalYawDeg: 'Gimbal yaw [deg]',
  stickAileron: 'Stick aileron',
  stickElevator: 'Stick elevator',
  stickRudder: 'Stick rudder',
  stickThrottle: 'Stick throttle',
  windSpeedMs: 'Wind speed [m/s]',
  windGustMs: 'Wind gust [m/s]',
  windFromDeg: 'Wind from [deg]',
  windRelativeDeg: 'Wind relative [deg]',
  flightModeCode: 'Flight mode code',
  motorsOn: 'Motors on',
  notEnoughForce: 'Insufficient motor power',
  vpsAltitudeM: 'VPS height [m]',
  batteryCell1V: 'Cell 1 [V]',
  batteryCell2V: 'Cell 2 [V]',
  batteryCell3V: 'Cell 3 [V]',
  batteryCell4V: 'Cell 4 [V]',
  batteryCell5V: 'Cell 5 [V]',
  batteryCell6V: 'Cell 6 [V]',
  flyingState: 'Flying state',
};

export function toCsv(flight) {
  const populated = Object.keys(flight.cols).filter((name) => flight.has(name));

  const ordered = [
    ...LEADING.filter((name) => populated.includes(name)),
    ...populated.filter((name) => !LEADING.includes(name)).sort(),
  ];

  const header = [
    ...ordered.map((name) => HEADINGS[name] || name),
    'Flight mode name',
    'Message',
  ];

  const lines = [header.map(escape).join(',')];

  // Events index only at their start row, so build a per-row lookup once
  // rather than scanning the list for every one of eighteen thousand rows.
  const messages = new Map();

  for (const event of flight.events) {
    const existing = messages.get(event.index);
    messages.set(event.index, existing ? `${existing} · ${event.text}` : event.text);
  }

  for (let i = 0; i < flight.count; i++) {
    const cells = ordered.map((name) => {
      const value = flight.cols[name][i];

      if (value === null || value === undefined) return '';
      if (name === 'epochS') return new Date(value * 1000).toISOString();

      return typeof value === 'number' ? String(round(value)) : String(value);
    });

    cells.push(flight.modeLabel(flight.cols.flightModeCode?.[i]) || '');
    cells.push(messages.get(i) || '');

    lines.push(cells.map(escape).join(','));
  }

  return lines.join('\r\n');
}

/**
 * Filename for the download.
 *
 * Keeps the original log's name so an exported CSV can still be traced back
 * to the file it came from, which matters when someone has a folder of them.
 */
export function csvFilename(flight) {
  return exportFilename(flight, 'csv');
}

/** Filename for an export, from the log's name or its flight date. */
export function exportFilename(flight, ext) {
  const source = flight.meta.filename;

  if (source) {
    return `${source.replace(/\.[^.]+$/, '')}.${ext}`;
  }

  const stamp = fmt.date(flight.meta.flownAt, flight.meta.timeZone).replace(/\s+/g, '-');
  return `flight-${stamp}.${ext}`;
}

/**
 * @param {string} filename
 * @param {string|Blob} content text, or a Blob for binary such as a PDF
 */
export function download(filename, content, mime = 'text/csv;charset=utf-8') {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();

  // Revoking immediately cancels the download in some browsers, so give the
  // navigation a moment to start.
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

const NUMERIC = /^-?\d+(\.\d+)?([eE][+-]?\d+)?$/;

/**
 * Excel and Sheets treat a leading =, +, - or @ as the start of a formula, so
 * a message beginning with one becomes executable content in the recipient's
 * spreadsheet. Prefixing a quote neutralises it.
 *
 * The numeric check is not optional. Every western longitude, every negative
 * pitch and every descent rate begins with a minus sign, and quoting those
 * turns the entire dataset into text: "-0.12776" arrives in the spreadsheet as
 * "'-0.12776" and will not plot, sum or sort. Escape genuine formulas, leave
 * numbers alone.
 */
function escape(value) {
  let text = value === null || value === undefined ? '' : String(value);

  if (/^[=+\-@\t\r]/.test(text) && !NUMERIC.test(text)) text = `'${text}`;
  if (/[",\r\n]/.test(text)) text = `"${text.replace(/"/g, '""')}"`;

  return text;
}

function round(value) {
  return Number.isInteger(value) ? value : Math.round(value * 1e6) / 1e6;
}
